import type { MetricValue, PermissionCode, RoleCode, SyncDisplayStatus } from '@oxxean/types';

/**
 * Contratos que el frontend consume y que aún no viven en `packages/types`.
 *
 * Están aquí, y no duplicados por pantalla, para que el día en que el backend
 * publique sus DTO baste con reemplazar este archivo por reexportaciones de
 * `@oxxean/types`. Nada de lo declarado aquí contiene datos: son sólo formas.
 */

// ─── Catálogos y filtros ────────────────────────────────────────────────────

export interface CatalogOption {
  id: string;
  label: string;
  /**
   * Empresa a la que pertenece la opción, cuando es dependiente.
   *
   * Presente en `areasByCompany`. Permite encadenar el desplegable de área al de
   * empresa: sin él, el formulario ofrecía áreas de otras empresas y el par
   * resultante era incoherente.
   */
  companyId?: string;
}

export interface FilterOptions {
  years: number[];
  companies: CatalogOption[];
  /**
   * Áreas para FILTRAR: una por nombre.
   *
   * En el filtro, «Operaciones» significa «las Operaciones del grupo». El backend
   * expande el identificador a todas las áreas homónimas del ámbito, así que
   * filtrar por Operaciones suma las cinco empresas y no una sola.
   */
  areas: CatalogOption[];
  /**
   * Áreas para ASIGNAR: todas, cada una con su `companyId`.
   *
   * Es una lista distinta porque responde a otra pregunta. Un caso de relaciones
   * laborales pertenece a UN área de UNA empresa, y con la lista deduplicada era
   * imposible elegirla bien: el identificador de «Operaciones» era el de una
   * empresa arbitraria. Los formularios encadenan con `useCompanyAreas`.
   */
  areasByCompany: CatalogOption[];
  costCenters: CatalogOption[];
  locations: CatalogOption[];
  jobRoles: CatalogOption[];
  contractTypes: CatalogOption[];
  employmentStatuses: CatalogOption[];
}

// ─── Tablas de indicadores ──────────────────────────────────────────────────

/** Una celda puede traer un `MetricValue`: el estado del dato llega hasta la fila. */
export type TableCellValue = string | number | boolean | null | MetricValue;

export interface IndicatorRow {
  id: string;
  [column: string]: TableCellValue | undefined;
}

export function isMetricValue(value: TableCellValue | undefined): value is MetricValue {
  if (typeof value !== 'object' || value === null) return false;
  return 'status' in value && 'value' in value;
}

// ─── Datos propios (§19): RRLL, fiscalizaciones y hallazgos ─────────────────

export const CASE_RISKS = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'] as const;
export type CaseRisk = (typeof CASE_RISKS)[number];

export const CASE_STATUSES = ['OPEN', 'IN_PROGRESS', 'CLOSED', 'CANCELLED'] as const;
export type CaseStatus = (typeof CASE_STATUSES)[number];

export const RISK_LABEL: Record<CaseRisk, string> = {
  LOW: 'Bajo',
  MEDIUM: 'Medio',
  HIGH: 'Alto',
  CRITICAL: 'Crítico',
};

export const CASE_STATUS_LABEL: Record<CaseStatus, string> = {
  OPEN: 'Abierto',
  IN_PROGRESS: 'En gestión',
  CLOSED: 'Cerrado',
  CANCELLED: 'Anulado',
};

/** Campos del §19 para relaciones laborales. */
export interface LaborRelationsCase {
  id: string;
  companyId: string | null;
  companyLabel: string;
  areaId: string;
  areaLabel: string;
  caseType: string;
  occurredOn: string;
  description: string;
  risk: CaseRisk;
  responsible: string;
  action: string;
  dueDate: string | null;
  status: CaseStatus;
  comment: string | null;
  evidenceReference: string | null;
  closedOn: string | null;
  createdAt: string;
  updatedAt: string;
  createdByName: string | null;
}

export interface LaborRelationsCaseInput {
  companyId: string | null;
  areaId: string | null;
  caseType: string;
  occurredOn: string;
  description: string;
  risk: CaseRisk;
  responsible: string;
  action: string;
  dueDate: string | null;
  status: CaseStatus;
  comment: string | null;
  evidenceReference: string | null;
  closedOn: string | null;
}

export interface InspectionObservation {
  id: string;
  companyId: string | null;
  companyLabel: string;
  areaId: string;
  areaLabel: string;
  /** Organismo fiscalizador (Dirección del Trabajo, SEREMI, mutualidad…). */
  authority: string;
  inspectionType: string;
  inspectedOn: string;
  subject: string;
  observation: string;
  risk: CaseRisk;
  responsible: string;
  action: string;
  dueDate: string | null;
  status: CaseStatus;
  fineAmount: number | null;
  evidenceReference: string | null;
  closedOn: string | null;
  createdAt: string;
  updatedAt: string;
  createdByName: string | null;
}

export interface InspectionObservationInput {
  companyId: string | null;
  areaId: string | null;
  authority: string;
  inspectionType: string;
  inspectedOn: string;
  subject: string;
  observation: string;
  risk: CaseRisk;
  responsible: string;
  action: string;
  dueDate: string | null;
  status: CaseStatus;
  fineAmount: number | null;
  evidenceReference: string | null;
  closedOn: string | null;
}

export type FindingSource = 'AUTOMATIC' | 'MANUAL';

export const FINDING_SOURCE_LABEL: Record<FindingSource, string> = {
  AUTOMATIC: 'Regla automática',
  MANUAL: 'Registro manual',
};

export interface Finding {
  id: string;
  companyId: string | null;
  companyLabel: string;
  areaId: string;
  areaLabel: string;
  periodLabel: string;
  indicatorKey: string;
  indicatorLabel: string;
  /** Resultado que disparó el hallazgo, con su estado de dato. */
  result: MetricValue;
  ruleCode: string | null;
  source: FindingSource;
  risk: CaseRisk;
  action: string;
  responsible: string;
  dueDate: string | null;
  status: CaseStatus;
  comment: string | null;
  detectedOn: string;
  closedOn: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface FindingInput {
  companyId: string | null;
  areaId: string | null;
  indicatorKey: string;
  indicatorLabel: string;
  risk: CaseRisk;
  action: string;
  responsible: string;
  dueDate: string | null;
  status: CaseStatus;
  comment: string | null;
  detectedOn: string;
  closedOn: string | null;
}

// ─── Sincronizaciones ───────────────────────────────────────────────────────

export type SyncRunStatus = 'RUNNING' | 'SUCCESS' | 'PARTIAL' | 'FAILED' | 'ABORTED';

export const SYNC_RUN_STATUS_LABEL: Record<SyncRunStatus, string> = {
  RUNNING: 'En ejecución',
  SUCCESS: 'Completada',
  PARTIAL: 'Parcial',
  FAILED: 'Con errores',
  ABORTED: 'Abortada',
};

export type SyncSource = 'BUK_CORE' | 'BUK_ATTENDANCE';

export const SYNC_SOURCE_LABEL: Record<SyncSource, string> = {
  BUK_CORE: 'BUK Core',
  BUK_ATTENDANCE: 'BUK Asistencia',
};

export interface SyncRun {
  id: string;
  source: SyncSource;
  sourceLabel: string;
  resource: string;
  periodFrom: string | null;
  periodTo: string | null;
  status: SyncRunStatus;
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
  recordsRead: number;
  recordsCreated: number;
  recordsUpdated: number;
  recordsSkipped: number;
  recordsFailed: number;
  triggeredBy: string | null;
  trigger: 'SCHEDULED' | 'MANUAL' | 'WEBHOOK';
  errorCount: number;
}

export interface SyncRunError {
  id: string;
  occurredAt: string;
  resource: string;
  page: number | null;
  code: string;
  /** Mensaje ya sanitizado por el backend. Se muestra íntegro: no se oculta. */
  message: string;
  externalId: string | null;
}

export interface SyncExecuteRequest {
  source: SyncSource;
  from?: string;
  to?: string;
  full?: boolean;
}

export interface SyncExecuteResponse {
  runId: string;
  status: SyncRunStatus;
  message: string;
}

export type { SyncDisplayStatus };

// ─── Administración ─────────────────────────────────────────────────────────

export interface UserListItem {
  id: string;
  email: string;
  fullName: string;
  roles: RoleCode[];
  active: boolean;
  mustChangePassword: boolean;
  lastLoginAt: string | null;
  companyScope: CatalogOption[];
  areaScope: CatalogOption[];
  createdAt: string;
}

export interface UserInput {
  email: string;
  fullName: string;
  roles: RoleCode[];
  active: boolean;
  companyScopeIds: string[];
  areaScopeIds: string[];
}

export interface RoleItem {
  code: string;
  name: string;
  description: string | null;
  hierarchyLevel: number;
  system: boolean;
  permissions: PermissionCode[];
  userCount: number;
}

export interface AuditEntry {
  id: string;
  occurredAt: string;
  actorName: string | null;
  actorEmail: string | null;
  action: string;
  module: string;
  entity: string | null;
  entityId: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  correlationId: string | null;
  summary: string | null;
}

export interface AnalyticsParameter {
  id: string;
  key: string;
  label: string;
  scope: 'AREA' | 'COMPANY' | 'GLOBAL';
  greenThreshold: number | null;
  yellowThreshold: number | null;
  lowerIsBetter: boolean;
  validFrom: string;
  validTo: string | null;
  pending: boolean;
  note: string | null;
}

export interface IntegrationStatus {
  key: string;
  label: string;
  configured: boolean;
  baseUrlMasked: string | null;
  lastCheckAt: string | null;
  healthy: boolean | null;
  note: string | null;
}

export interface SettingsResponse {
  parameters: AnalyticsParameter[];
  integrations: IntegrationStatus[];
  timezone: string;
  initialSyncFrom: string | null;
}
